import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import clsx from 'clsx'
import { CircularProgress } from '@material-ui/core'
import FadeIn from '../../../molecules/Transitions/FadeIn'
import { KEYS } from '../../../../constants'
import SelectPopoverMenu from './SelectPopoverMenu'

function useOptionsFilter (searchText, options, pinnedOptions) {
  return useMemo(() => {
    const filteredPinned = (pinnedOptions || []).filter(x => x.label.toLowerCase().includes(searchText.toLowerCase()))
    const filteredOptions = (options || []).filter(x => x.label.toLowerCase().includes(searchText.toLowerCase()))

    return [filteredOptions, filteredPinned]
  }, [searchText, options, pinnedOptions])
}

function ScopeSelect ({
  options,
  pinnedOptions,
  onSelect,
  className,
  style,
  isLoading,
  children
}) {
  const anchorRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [filteredOptions, filteredPinnedOptions] = useOptionsFilter(searchText, options, pinnedOptions)

  const handleToggle = useCallback(() => {
    setOpen(p => !p)
    setSearchText('')
  }, [setOpen, setSearchText])

  const handleSelect = useCallback((scope) => {
    onSelect(scope)
    setOpen(false)
  }, [onSelect, setOpen])

  const handleEnter = useCallback((e) => {
    if (open && e.which === KEYS.ENTER) {
      const combined = [...(filteredPinnedOptions || []), ...(filteredOptions || [])]
      if (combined.length === 1) {
        handleSelect(combined.at(0))
        e.stopPropagation()
        e.preventDefault()
      }
    }
  }, [open, filteredOptions, filteredPinnedOptions, handleSelect])

  const handleEscape = useCallback((e) => {
    if (open && e.which === KEYS.ESC) {
      handleToggle()
      e.stopPropagation()
      e.preventDefault()
    }
  }, [open, handleToggle])

  // hotkey handlers
  useEffect(() => {
    const handler = (e) => {
      handleEscape(e)
      handleEnter(e)
    }
    window.addEventListener('keydown', handler, false)
    return () => {
      window.removeEventListener('keydown', handler, false)
    }
  }, [handleEscape, handleEnter])

  return (
    <div>
      <div
        role='button'
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        className={clsx('flex gap-2 items-center opal-surface-default hover select-none', className)}
        style={style}
      >
        {isLoading ? (
          <CircularProgress size={54} />
        ) : (
          <>
            <FadeIn className='flex-auto'>
              {children}
            </FadeIn>
            <FadeIn className='flex ml-2'>
              {open ? (
                <ExpandMoreIcon fontSize='small' />
              ) : (
                <ExpandLessIcon fontSize='small' />
              )}
            </FadeIn>
          </>
        )}
      </div>
      <SelectPopoverMenu
        anchorEl={anchorRef.current}
        open={open}
        setOpen={setOpen}
        options={filteredOptions}
        pinnedOptions={filteredPinnedOptions}
        onSearchChange={setSearchText}
        onSelect={handleSelect}
      />
    </div>
  )
}

ScopeSelect.propTypes = {
  options: PropTypes.array,
  pinnedOptions: PropTypes.array,
  onSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
}

export default ScopeSelect
